import { useEffect, useState } from "react";
import { api, errText } from "../../lib/api";
import { ClaudeModelSelect } from "./ClaudeModelSelect";
import { SearchVisibilityPanel } from "./SearchVisibilityPanel";

const GROUPS = [
  ["payments", "Payments & gateways"],
  ["pricing", "Pricing & card fees"],
  ["support", "Support contact"],
  ["reminders", "Reminders & nudges"],
  ["providers", "Email, SMS & WhatsApp"],
  ["alerts", "Alert wording"],
  ["seo", "SEO & feeds"],
  ["operations", "Order routing"],
  ["cutoffs", "Same-day cut-offs"],
  ["maps", "Maps & location"],
  ["company", "Company & legal"],
  ["security", "Security & domains"],
  ["danger", "Danger zone"],
];

const Field = ({ label, hint, testid, value, onChange, type = "text", options }) => (
  <label className="block space-y-1">
    <span className="smallcaps">{label}</span>
    {options ? (
      <select className="field" data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    ) : (
      <input className="field" type={type} data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} />
    )}
    {hint ? <span className="block text-xs text-muted">{hint}</span> : null}
  </label>
);

const CityCutoffEditor = ({ defaults }) => {
  const [cities, setCities] = useState(null);
  const [saving, setSaving] = useState(null);
  const [msg, setMsg] = useState("");
  useEffect(() => { api.get("/admin/cities").then((r) => setCities(r.data.cities)).catch(() => setCities([])); }, []);
  const setWin = (id, win, v) => setCities((cs) => cs.map((c) => (c.id === id ? { ...c, window_cutoffs: { ...c.window_cutoffs, [win]: v } } : c)));
  const save = async (c) => {
    setSaving(c.id); setMsg("");
    try {
      await api.put(`/admin/cities/${c.id}/cutoffs`, { window_cutoffs: c.window_cutoffs });
      setMsg(`Saved ${c.name}. Storefront uses it immediately.`);
    } catch (e) { setMsg(errText(e)); } finally { setSaving(null); }
  };
  if (!cities) return <p className="text-sm text-muted">Loading cities…</p>;
  return (
    <div className="space-y-3" data-testid="city-cutoffs">
      <p className="text-xs text-muted">Leave a field blank to inherit the global default (shown as the placeholder). Format HH:MM, 24-hour Morocco time.</p>
      {cities.map((c) => (
        <div key={c.id} className="border-b border-brass-light pb-3" data-testid={`city-cutoff-${c.id}`}>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-sm text-ink">{c.name}</span>
            <button className="btn-ghost text-xs px-3" data-testid={`city-save-${c.id}`} disabled={saving === c.id} onClick={() => save(c)}>
              {saving === c.id ? "Saving…" : "Save"}
            </button>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {[["morning", "Morning"], ["afternoon", "Afternoon"], ["evening", "Evening"]].map(([w, lbl]) => (
              <label key={w} className="block space-y-1">
                <span className="text-[11px] text-muted">{lbl}</span>
                <input className="field" data-testid={`city-${c.id}-${w}`} placeholder={defaults[w] || "17:00"}
                       value={c.window_cutoffs[w] || ""} onChange={(e) => setWin(c.id, w, e.target.value)} />
              </label>
            ))}
          </div>
        </div>
      ))}
      {msg ? <p className="text-sm text-brass" data-testid="city-cutoffs-msg">{msg}</p> : null}
    </div>
  );
};

export const SettingsPanel = ({ settings, onSaved }) => {
  const [claudeModel, setClaudeModel] = useState("");
  const [group, setGroup] = useState("payments");
  const [form, setForm] = useState(() => {
    const keys = ["support_whatsapp", "support_hours", "support_email", "nudges_enabled", "nudge_lead_days",
      "gateway_fee_percent", "gateway_fee_fixed_mad", "gateway_fee_basis", "gateway_fee_scope",
      "capture_mode", "statement_descriptor", "refund_window_days", "offer_expiry_minutes",
      "admin_session_hours", "mfa_trust_hours",
      "max_offers_before_escalation", "legal_name", "legal_address", "vat_id",
      "driver_payout_day", "ops_alert_phone", "ops_alert_whatsapp", "stuck_hold_alert_minutes", "network_assign_lead_minutes",
      "cutoff_morning", "cutoff_afternoon", "cutoff_evening",
      "google_maps_api_key", "google_maps_signing_secret", "geolocation_enabled", "address_autocomplete_enabled", "delivery_pin_enabled",
      "payment_gateway", "subscriptions_enabled", "subscription_discount_percent", "custom_dates_enabled",
      "onboarding_booking_url", "extra_allowed_origins"];
    return Object.fromEntries(keys.map((k) => [k, settings[k] ?? ""]));
  });
  const [msg, setMsg] = useState("");
  const [testTo, setTestTo] = useState("");
  const [testPhone, setTestPhone] = useState("");
  const [nudges, setNudges] = useState(null);
  const [sales, setSales] = useState(null);
  const [stripe, setStripe] = useState(settings.stripe || null);
  const [keys, setKeys] = useState({ test_secret: "", test_publishable: "", live_secret: "", live_publishable: "" });
  const [tryb, setTryb] = useState(settings.tryb || null);
  const [tkeys, setTkeys] = useState({ test_secret: "", live_secret: "", test_webhook_secret: "", live_webhook_secret: "" });
  const [msgs, setMsgs] = useState(settings.messaging || null);
  const [testWa, setTestWa] = useState("");
  const [alertsCfg, setAlertsCfg] = useState(null);
  const [aform, setAform] = useState({});
  const [seo, setSeo] = useState(null);
  const [aiKey, setAiKey] = useState("");
  const [missingOnly, setMissingOnly] = useState(false);
  const [writing, setWriting] = useState(null);
  const [mform, setMform] = useState({ infobip_api_key: "", infobip_base_url: "", infobip_sms_sender: "",
    infobip_whatsapp_sender: "", twilio_account_sid: "", twilio_auth_token: "", twilio_from_number: "",
    twilio_messaging_service_sid: "", meta_access_token: "", meta_phone_number_id: "", meta_template_name: "",
    resend_api_key: "", sender_email: "", sender_name: "" });

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setMsg("");
    try {
      await api.put("/admin/settings", form);
      setMsg("Saved. The storefront and the apps use these values immediately.");
      onSaved();
    } catch (e) {
      setMsg(errText(e));
    }
  };

  const sendTest = async () => {
    setMsg("");
    try {
      const { data } = await api.post("/admin/settings/test-email", { to: testTo });
      setMsg(data.ok ? `Test email sent to ${testTo}.` : `Not sent: ${data.reason}`);
    } catch (e) {
      setMsg(errText(e));
    }
  };

  const sendTestSms = async () => {
    setMsg("");
    try {
      const { data } = await api.post("/admin/settings/test-sms", { to: testPhone });
      setMsg(data.ok ? `Test alert sent to ${testPhone}.` : `Not sent: ${data.reason}`);
    } catch (e) {
      setMsg(errText(e));
    }
  };

  const previewNudges = async () => {
    setMsg("");
    try {
      const { data } = await api.post("/admin/nudges/run?dry_run=true");
      setNudges(data);
    } catch (e) {
      setMsg(errText(e));
    }
  };

  const saveKeys = async (extra = {}) => {
    setMsg("");
    const body = Object.fromEntries(Object.entries({ ...keys, ...extra }).filter(([, v]) => v));
    if (!Object.keys(body).length) {
      setMsg("Paste a key first.");
      return;
    }
    try {
      const { data } = await api.put("/admin/settings/stripe", body);
      setStripe(data);
      setKeys({ test_secret: "", test_publishable: "", live_secret: "", live_publishable: "" });
      setMsg(`Saved. Fleurinelle is taking payments in ${data.mode} mode.`);
      onSaved();
    } catch (e) {
      const detail = errText(e);
      const rejected = /rejected|start with/i.test(detail);
      if (rejected) setKeys((k) => ({ ...k, test_secret: "", live_secret: "" }));
      setMsg(rejected ? `${detail} — the secret field has been cleared, paste it again.` : detail);
    }
  };

  const verifyKey = async () => {
    setMsg("");
    try {
      const { data } = await api.post("/admin/settings/stripe/verify");
      setMsg(data.ok
        ? `Key works · account ${data.account_id} (${data.country}) · charges ${data.charges_enabled ? "enabled" : "not enabled yet"}`
        : `Stripe says: ${data.reason}`);
    } catch (e) {
      setMsg(errText(e));
    }
  };

  const removeKeys = async (which) => {
    setMsg("");
    try {
      const { data } = await api.delete(`/admin/settings/stripe/${which}`);
      setStripe(data);
      setMsg(`${which} keys removed. Back to test mode.`);
      onSaved();
    } catch (e) {
      setMsg(errText(e));
    }
  };

  const saveTryb = async (extra = {}) => {
    setMsg("");
    const body = Object.fromEntries(Object.entries({ ...tkeys, ...extra }).filter(([, v]) => v));
    if (!Object.keys(body).length) {
      setMsg("Paste a TRYB key first.");
      return;
    }
    try {
      const { data } = await api.put("/admin/settings/tryb", body);
      setTryb(data);
      setTkeys({ test_secret: "", live_secret: "", test_webhook_secret: "", live_webhook_secret: "" });
      setMsg(`Saved. TRYB Billing is in ${data.mode} mode.`);
      onSaved();
    } catch (e) {
      const detail = errText(e);
      if (/rejected|start with/i.test(detail)) setTkeys((k) => ({ ...k, test_secret: "", live_secret: "" }));
      setMsg(detail);
    }
  };

  const verifyTryb = async () => {
    setMsg("");
    try {
      const { data } = await api.post("/admin/settings/tryb/verify");
      setMsg(data.ok ? `TRYB key works · ${data.livemode ? "live" : "test"} mode` : `TRYB says: ${data.reason}`);
    } catch (e) {
      setMsg(errText(e));
    }
  };

  const removeTryb = async (which) => {
    setMsg("");
    try {
      const { data } = await api.delete(`/admin/settings/tryb/${which}`);
      setTryb(data);
      setMsg(`TRYB ${which} keys removed.`);
      onSaved();
    } catch (e) {
      setMsg(errText(e));
    }
  };

  const saveMessaging = async (extra = {}) => {
    setMsg("");
    const body = Object.fromEntries(Object.entries({ ...mform, ...extra }).filter(([, v]) => v));
    if (!Object.keys(body).length) {
      setMsg("Fill in a key or pick a provider first.");
      return;
    }
    try {
      const { data } = await api.put("/admin/settings/messaging", body);
      setMsgs(data);
      setMform((f) => Object.fromEntries(Object.keys(f).map((k) => [k, ""])));
      setMsg("Saved. New order alerts go out through these providers.");
      onSaved();
    } catch (e) {
      setMsg(errText(e));
    }
  };

  const removeMessaging = async (provider) => {
    setMsg("");
    try {
      const { data } = await api.delete(`/admin/settings/messaging/${provider}`);
      setMsgs(data);
      setMsg(`${provider} keys removed.`);
      onSaved();
    } catch (e) {
      setMsg(errText(e));
    }
  };

  const sendTestWhatsapp = async () => {
    setMsg("");
    try {
      const { data } = await api.post("/admin/settings/test-whatsapp", { to: testWa });
      setMsg(data.ok ? `Test WhatsApp sent to ${testWa} via ${data.provider}.` : `Not sent: ${data.reason}`);
    } catch (e) {
      setMsg(errText(e));
    }
  };

  useEffect(() => {
    if (group === "alerts" && !alertsCfg) api.get("/admin/settings/alerts").then((r) => setAlertsCfg(r.data)).catch(() => {});
    if (group === "seo" && !seo) loadSeo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group]);

  const loadSeo = async (missing = missingOnly) => {
    try {
      const { data } = await api.get("/admin/seo/products", { params: { missing_only: missing } });
      setSeo(data);
    } catch (e) {
      setMsg(errText(e));
    }
  };

  const saveAiKey = async () => {
    setMsg("");
    try {
      await api.put("/admin/seo/ai", { api_key: aiKey });
      setAiKey("");
      await loadSeo();
      setMsg("Claude connected. You can write metadata for any bouquet now.");
    } catch (e) {
      setAiKey("");
      setMsg(errText(e));
    }
  };

  const removeAiKey = async () => {
    setMsg("");
    try {
      await api.delete("/admin/seo/ai");
      await loadSeo();
      setMsg("Key removed.");
    } catch (e) {
      setMsg(errText(e));
    }
  };

  const generateSeo = async (productId) => {
    setWriting(productId);
    setMsg("");
    try {
      const { data } = await api.post(`/admin/seo/products/${productId}/generate`, { model: claudeModel || null });
      setMsg(`Written: ${data.locales?.en?.meta_title || "done"}`);
      await loadSeo();
    } catch (e) {
      setMsg(errText(e));
    }
    setWriting(null);
  };

  const previewAlert = (template) => {
    const sample = { shop: "Maison Nour", owner: "Nadia", order: "FL-2026-1042", date: "2026-06-21",
      window: "14:00 – 18:00", city: "Casablanca", minutes: "15", recipient: "Amina", total: "540 MAD" };
    return Object.entries(sample).reduce((out, [k, v]) => out.split(`{${k}}`).join(v), template || "");
  };

  const saveAlerts = async () => {
    setMsg("");
    const body = Object.fromEntries(Object.entries(aform).filter(([, v]) => v && v.trim()));
    if (!Object.keys(body).length) {
      setMsg("Change a message first.");
      return;
    }
    try {
      const { data } = await api.put("/admin/settings/alerts", body);
      setAlertsCfg(data);
      setAform({});
      setMsg("Saved. Shops will read this from the next order onwards.");
    } catch (e) {
      setMsg(errText(e));
    }
  };

  const resetAlert = async (name) => {
    setMsg("");
    try {
      const { data } = await api.delete(`/admin/settings/alerts/${name}`);
      setAlertsCfg(data);
      setAform((f) => ({ ...f, [name]: undefined }));
      setMsg(`${data.labels[name]} is back to the standard wording.`);
    } catch (e) {
      setMsg(errText(e));
    }
  };

  const loadSales = async () => {
    try {
      const { data } = await api.get("/admin/shop-sales");
      setSales(data);
    } catch (e) {
      setMsg(errText(e));
    }
  };

  const SaveRow = ({ testid }) => (
    <div className="flex gap-2 items-center pt-1">
      <button className="btn-primary text-xs px-4" data-testid={testid} onClick={save}>Save settings</button>
      {msg ? <span className="text-xs text-ink" data-testid="settings-message">{msg}</span> : null}
    </div>
  );

  return (
    <div className="flex flex-col lg:flex-row gap-6" data-testid="admin-settings">
      <nav className="lg:w-56 flex lg:flex-col gap-1 overflow-x-auto">
        {GROUPS.map(([key, label]) => (
          <button key={key} data-testid={`settings-group-${key}`} onClick={() => setGroup(key)}
                  className={`text-start text-sm px-3 py-2 whitespace-nowrap border-s-2 transition-colors ${group === key ? "border-brass text-ink bg-plaster/60" : "border-transparent text-muted hover:text-ink"}`}>
            {label}
          </button>
        ))}
      </nav>

      <div className="flex-1 space-y-6 max-w-2xl">
        {group === "payments" ? (
          <div className="space-y-6" data-testid="settings-payments">
            <div className="card p-5 space-y-4">
              <h3 className="smallcaps">Active gateway</h3>
              <p className="text-sm" data-testid="active-gateway-status">
                Payments are going through <span className="text-ink">{settings.active_gateway === "tryb" ? "TRYB Billing" : "Stripe"}</span>.
                {form.payment_gateway === "tryb" && !tryb?.configured
                  ? " TRYB is selected but no key is saved yet, so Stripe is still taking the money."
                  : ""}
              </p>
              <Field label="Take payments through" testid="settings-payment-gateway" value={form.payment_gateway || "stripe"} onChange={set("payment_gateway")}
                     options={[["stripe", "Stripe"], ["tryb", "TRYB Billing"]]}
                     hint="Applies to new checkouts and new recurring plans. Plans already running stay on the gateway they were signed up with." />
              <SaveRow testid="settings-save-gateway" />
            </div>

            <div className="card p-5 space-y-4">
              <h3 className="smallcaps">Recurring deliveries</h3>
              <Field label="Recurring plans" testid="settings-subscriptions-enabled" value={form.subscriptions_enabled || "on"} onChange={set("subscriptions_enabled")}
                     options={[["on", "Offered at checkout (weekly, monthly, yearly, own dates)"], ["off", "Switched off"]]}
                     hint="Recurring plans need your own gateway key — Stripe secret key or TRYB secret key — because a card has to be kept on file." />
              <Field label="Recurring discount (%)" testid="settings-subscription-discount" value={form.subscription_discount_percent} onChange={set("subscription_discount_percent")}
                     hint="Applied to bouquets on recurring deliveries. Fleurinelle funds the discount; partner payouts stay fixed." />
              <Field label="Customer-picked dates" testid="settings-custom-dates-enabled" value={form.custom_dates_enabled || "on"} onChange={set("custom_dates_enabled")}
                     options={[["on", "Offered — customers can pick their own delivery dates"], ["off", "Hidden — only weekly, monthly and yearly"]]}
                     hint="Picked dates need the gateway to store a card. Leave this off while TRYB's save-card page is failing; weekly, monthly and yearly are unaffected." />
              <SaveRow testid="settings-save-recurring" />
            </div>

            <div className="card p-5 space-y-4">
              <h3 className="smallcaps">TRYB Billing keys</h3>
              <p className="text-sm" data-testid="tryb-status">
                {tryb?.has_test_key || tryb?.has_live_key
                  ? `Test ${tryb.has_test_key ? `saved (····${tryb.test_key_last4})` : "not saved"} · live ${tryb.has_live_key ? `saved (····${tryb.live_key_last4})` : "not saved"} · currently ${tryb.mode} mode`
                  : "No TRYB key saved yet. Paste your per-site secret key from billing.tryb.business."}
              </p>
              <div className="flex items-center gap-3 flex-wrap" data-testid="tryb-mode-row">
                <span className="smallcaps">Mode</span>
                <button className={`text-xs px-4 py-1.5 border transition-colors ${tryb?.mode === "test" ? "bg-ink text-plaster border-ink" : "border-brass text-muted hover:text-ink"}`}
                        data-testid="tryb-mode-test" onClick={() => saveTryb({ mode: "test" })}>Test</button>
                <button className={`text-xs px-4 py-1.5 border transition-colors ${tryb?.mode === "live" ? "bg-ink text-plaster border-ink" : "border-brass text-muted hover:text-ink"}`}
                        data-testid="tryb-mode-live" onClick={() => saveTryb({ mode: "live" })}>Live</button>
              </div>
              <div className="space-y-2">
                <p className="smallcaps">Test</p>
                <input className="field" placeholder="tryb_sk_test_…" data-testid="tryb-test-secret" autoComplete="off"
                       value={tkeys.test_secret} onChange={(e) => setTkeys({ ...tkeys, test_secret: e.target.value.trim() })} />
                <input className="field" placeholder="Test webhook signing secret" data-testid="tryb-test-webhook" autoComplete="off"
                       value={tkeys.test_webhook_secret} onChange={(e) => setTkeys({ ...tkeys, test_webhook_secret: e.target.value.trim() })} />
              </div>
              <div className="space-y-2">
                <p className="smallcaps">Live</p>
                <input className="field" placeholder="tryb_sk_live_…" data-testid="tryb-live-secret" autoComplete="off"
                       value={tkeys.live_secret} onChange={(e) => setTkeys({ ...tkeys, live_secret: e.target.value.trim() })} />
                <input className="field" placeholder="Live webhook signing secret" data-testid="tryb-live-webhook" autoComplete="off"
                       value={tkeys.live_webhook_secret} onChange={(e) => setTkeys({ ...tkeys, live_webhook_secret: e.target.value.trim() })} />
              </div>
              <div className="flex flex-wrap gap-2">
                <button className="btn-primary text-xs px-4" data-testid="tryb-keys-save" onClick={() => saveTryb()}>Save TRYB keys</button>
                <button className="btn-ghost text-xs px-3" data-testid="tryb-keys-verify" onClick={verifyTryb}>Verify active key</button>
                {tryb?.has_test_key ? <button className="btn-ghost text-xs px-3" data-testid="tryb-remove-test" onClick={() => removeTryb("test")}>Remove test keys</button> : null}
                {tryb?.has_live_key ? <button className="btn-ghost text-xs px-3" data-testid="tryb-remove-live" onClick={() => removeTryb("live")}>Remove live keys</button> : null}
              </div>
              <p className="text-xs text-muted">
                Point TRYB's webhook at <span className="text-ink">{process.env.REACT_APP_BACKEND_URL}/api/webhook/tryb</span> and paste the signing
                secret above. Secrets are kept on the server and never shown again — only the last four characters.
              </p>
              {msg ? <p className="text-xs text-ink" data-testid="tryb-message">{msg}</p> : null}
              {tryb?.last_error ? (
                <p className="text-xs text-danger" data-testid="tryb-last-error">
                  Last error from TRYB ({tryb.last_error_path}): {tryb.last_error}
                </p>
              ) : null}
            </div>

            <div className="card p-5 space-y-4">
              <h3 className="smallcaps">Stripe keys</h3>
              <p className="text-sm" data-testid="stripe-status">
                {stripe?.has_test_key || stripe?.has_live_key
                  ? `Your own keys · test ${stripe.has_test_key ? `saved (····${stripe.test_key_last4})` : "not saved"} · live ${stripe.has_live_key ? `saved (····${stripe.live_key_last4})` : "not saved"} · currently ${stripe.mode} mode`
                  : "Using the shared Fleurinelle test key. Paste your own keys below to take real payments and enable card holds."}
              </p>
              <div className="flex items-center gap-3 flex-wrap" data-testid="stripe-mode-row">
                <span className="smallcaps">Mode</span>
                <button className={`text-xs px-4 py-1.5 border transition-colors ${stripe?.mode === "test" ? "bg-ink text-plaster border-ink" : "border-brass text-muted hover:text-ink"}`}
                        data-testid="stripe-mode-test" onClick={() => saveKeys({ mode: "test" })}>Test</button>
                <button className={`text-xs px-4 py-1.5 border transition-colors ${stripe?.mode === "live" ? "bg-ink text-plaster border-ink" : "border-brass text-muted hover:text-ink"}`}
                        data-testid="stripe-mode-live" onClick={() => saveKeys({ mode: "live" })}>Live</button>
                {stripe?.mode === "live" ? <span className="text-xs text-danger" data-testid="live-mode-warning">Real cards are being charged.</span> : null}
              </div>

              <div className="space-y-2">
                <p className="smallcaps">Test keys</p>
                <input className="field" placeholder="sk_test_…" data-testid="stripe-test-secret" autoComplete="off"
                       value={keys.test_secret} onChange={(e) => setKeys({ ...keys, test_secret: e.target.value.trim() })} />
                <input className="field" placeholder="pk_test_… (optional)" data-testid="stripe-test-publishable"
                       value={keys.test_publishable} onChange={(e) => setKeys({ ...keys, test_publishable: e.target.value.trim() })} />
              </div>

              <div className="space-y-2">
                <p className="smallcaps">Live keys</p>
                <input className="field" placeholder="sk_live_…" data-testid="stripe-live-secret" autoComplete="off"
                       value={keys.live_secret} onChange={(e) => setKeys({ ...keys, live_secret: e.target.value.trim() })} />
                <input className="field" placeholder="pk_live_… (optional)" data-testid="stripe-live-publishable"
                       value={keys.live_publishable} onChange={(e) => setKeys({ ...keys, live_publishable: e.target.value.trim() })} />
              </div>

              <div className="flex flex-wrap gap-2">
                <button className="btn-primary text-xs px-4" data-testid="stripe-keys-save" onClick={() => saveKeys()}>Save keys</button>
                <button className="btn-ghost text-xs px-3" data-testid="stripe-keys-verify" onClick={verifyKey}>Verify active key</button>
                {stripe?.has_test_key ? <button className="btn-ghost text-xs px-3" data-testid="stripe-remove-test" onClick={() => removeKeys("test")}>Remove test keys</button> : null}
                {stripe?.has_live_key ? <button className="btn-ghost text-xs px-3" data-testid="stripe-remove-live" onClick={() => removeKeys("live")}>Remove live keys</button> : null}
              </div>
              <p className="text-xs text-muted">
                Secret keys are kept on the server and never shown again — only the last four characters. Copy them from Stripe → Developers → API keys.
              </p>
              {msg ? <p className="text-xs text-ink" data-testid="stripe-message">{msg}</p> : null}
            </div>

            <div className="card p-5 space-y-4">
              <h3 className="smallcaps">Charging rules</h3>
              <Field label="Charge timing" testid="settings-capture-mode" value={form.capture_mode || "immediate"} onChange={set("capture_mode")}
                     options={[["immediate", "Charge in full at checkout"], ["preauth", "Hold the card at checkout, charge when the order goes out for delivery"]]}
                     hint={stripe?.preauth_supported
                       ? "Card holds are available on your Stripe account. The florist is told the payment cleared before setting out."
                       : "Card holds need your own Stripe secret key above. Until one is saved, orders are charged at checkout whatever this says."} />
              <Field label="Statement descriptor" testid="settings-statement-descriptor" value={form.statement_descriptor} onChange={set("statement_descriptor")}
                     hint="What the sender sees on their bank statement. Up to 22 characters." />
              <Field label="Refund window (days)" testid="settings-refund-window" value={form.refund_window_days} onChange={set("refund_window_days")}
                     hint="How long after delivery support can still issue a full refund without approval." />
              <p className="text-xs text-muted">Currencies accepted: {stripe?.currencies?.join(", ")}</p>
              <SaveRow testid="settings-save-payments" />
            </div>
          </div>
        ) : null}

        {group === "pricing" ? (
          <div className="card p-5 space-y-4" data-testid="settings-pricing">
            <h3 className="smallcaps">Fleurinelle pricing</h3>
            <p className="text-sm text-muted" data-testid="settings-fixed-pricing">Selling prices: EUR · Partner prices: MAD · Card fees: Fleurinelle</p>

            <div className="border-t border-brass-light pt-4 space-y-4">
              <h3 className="smallcaps">Payment fees (paid by Fleurinelle)</h3>
              <p className="text-xs text-muted">
                Whatever gateway is active — Stripe directly or TRYB Billing — the card fee is the same Stripe fee.
                Partner payouts remain the fixed prices set in the catalogue, regardless of discounts or card fees.
                Existing orders keep the fee they were placed with.
              </p>
              <Field label="Payment fee (%)" testid="settings-gateway-fee-percent" value={form.gateway_fee_percent} onChange={set("gateway_fee_percent")}
                     hint="3.25% is Stripe's rate for international cards, which is what most Fleurinelle senders use." />
              <Field label="Fixed fee per order (MAD)" testid="settings-gateway-fee-fixed" value={form.gateway_fee_fixed_mad} onChange={set("gateway_fee_fixed_mad")} />
              <Field label="The fee is calculated on" testid="settings-gateway-fee-basis" value={form.gateway_fee_basis || "charged"} onChange={set("gateway_fee_basis")}
                     options={[["charged", "Everything the buyer paid, delivery included — what the gateway really charges on"],
                               ["bouquet", "The bouquet and add-ons only"]]} />
              <SaveRow testid="settings-save-gateway-fees" />
            </div>
          </div>
        ) : null}

        {group === "support" ? (
          <div className="card p-5 space-y-4" data-testid="settings-support">
            <h3 className="smallcaps">Support contact shown on the storefront</h3>
            <Field label="WhatsApp number" testid="settings-whatsapp" value={form.support_whatsapp} onChange={set("support_whatsapp")}
                   hint="Leave empty and the footer falls back to the support email." />
            <Field label="Hours" testid="settings-hours" value={form.support_hours} onChange={set("support_hours")} />
            <Field label="Support email" testid="settings-email" value={form.support_email} onChange={set("support_email")} />
            <SaveRow testid="settings-save" />
          </div>
        ) : null}

        {group === "support" ? (
          <div className="card p-5 space-y-4" data-testid="settings-onboarding">
            <h3 className="smallcaps">Partner florist onboarding</h3>
            <Field label="Onboarding booking link" testid="settings-onboarding-booking-url"
                   value={form.onboarding_booking_url} onChange={set("onboarding_booking_url")}
                   hint="The 45-min Google Meet onboarding & Q&A booking link (e.g. Google Calendar appointments or Calendly). Newly accepted florists get a button to this link in their welcome e-mail. You can change it any time." />
            <SaveRow testid="settings-save-onboarding" />
          </div>
        ) : null}

        {group === "reminders" ? (
          <div className="card p-5 space-y-4" data-testid="settings-reminders">
            <h3 className="smallcaps">Yearly occasion reminders</h3>
            <Field label="Reminder emails" testid="settings-nudges-enabled" value={form.nudges_enabled || "on"} onChange={set("nudges_enabled")}
                   options={[["on", "Email past senders before the same occasion next year"], ["off", "Off"]]} />
            <Field label="Days of notice" testid="settings-nudge-lead-days" value={form.nudge_lead_days} onChange={set("nudge_lead_days")}
                   hint="Each email carries a personal single-use 5% thank-you code, funded by Fleurinelle." />
            <SaveRow testid="settings-save-nudges" />
            <div className="pt-1 space-y-2">
              <button className="btn-ghost text-xs px-3" data-testid="settings-nudges-preview" onClick={previewNudges}>Preview today's nudges</button>
              {nudges ? (
                <p className="text-xs text-muted" data-testid="nudges-preview">
                  {nudges.candidates} sender{nudges.candidates === 1 ? "" : "s"} would be emailed on {nudges.on_date} ({nudges.lead_days} days of notice).
                </p>
              ) : null}
            </div>
          </div>
        ) : null}

        {group === "providers" ? (
          <div className="space-y-6" data-testid="settings-providers">
            <div className="card p-5 space-y-3 text-sm">
              <h3 className="smallcaps">Email (Resend)</h3>
              <p data-testid="email-status">
                {msgs?.email_ready ? `connected · key ····${msgs.resend_api_key_last4}` : "no API key yet"} · sender {msgs?.sender_email || "not set"}
              </p>
              <input className="field" placeholder="Resend API key (re_…)" autoComplete="off" data-testid="resend-api-key"
                     value={mform.resend_api_key} onChange={(e) => setMform({ ...mform, resend_api_key: e.target.value.trim() })} />
              <div className="grid sm:grid-cols-2 gap-2">
                <input className="field" placeholder="Sender email, e.g. commandes@fleurinelle.com" data-testid="resend-sender-email"
                       value={mform.sender_email} onChange={(e) => setMform({ ...mform, sender_email: e.target.value.trim() })} />
                <input className="field" placeholder="Sender name (Fleurinelle)" data-testid="resend-sender-name"
                       value={mform.sender_name} onChange={(e) => setMform({ ...mform, sender_name: e.target.value })} />
              </div>
              <p className="text-xs text-muted">Create the key at resend.com → API Keys, and verify the sending domain there first — Resend refuses a from-address on an unverified domain.</p>
              <div className="flex flex-wrap gap-2">
                <button className="btn-primary text-xs px-4" data-testid="resend-save" onClick={() => saveMessaging()}>Save email keys</button>
                {msgs?.resend_api_key ? <button className="btn-ghost text-xs px-3" data-testid="resend-remove" onClick={() => removeMessaging("resend")}>Remove key</button> : null}
              </div>
              <div className="flex gap-2 border-t border-brass-light pt-3">
                <input className="field flex-1" placeholder="Send a test email to…" data-testid="settings-test-to"
                       value={testTo} onChange={(e) => setTestTo(e.target.value)} />
                <button className="btn-ghost text-xs px-3" data-testid="settings-test-send" onClick={sendTest} disabled={!testTo}>Send test</button>
              </div>
            </div>

            <div className="card p-5 space-y-4">
              <h3 className="smallcaps">SMS & WhatsApp providers</h3>
              <p className="text-xs text-muted" data-testid="sms-status">
                SMS: {msgs?.sms_provider === "none" ? "not configured" : msgs?.sms_provider} · WhatsApp: {msgs?.whatsapp_provider === "none" ? "not configured" : msgs?.whatsapp_provider}
              </p>

              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Send SMS through" testid="messaging-sms-provider" value={msgs?.sms_provider || "none"} onChange={(v) => saveMessaging({ sms_provider: v })}
                       hint="Only providers whose keys are saved below can be picked."
                       options={[["none", "Off"],
                                 ["twilio", msgs?.twilio_ready ? "Twilio" : "Twilio — add keys below first"],
                                 ["infobip", msgs?.infobip_sms_ready ? "Infobip" : "Infobip — add key and SMS sender below first"]]
                                 .filter(([v]) => v === "none" || (v === "twilio" ? msgs?.twilio_ready : msgs?.infobip_sms_ready) || v === msgs?.sms_provider)} />
                <Field label="Send WhatsApp through" testid="messaging-whatsapp-provider" value={msgs?.whatsapp_provider || "none"} onChange={(v) => saveMessaging({ whatsapp_provider: v })}
                       hint="Same here — save the provider keys, then pick it."
                       options={[["none", "Off"], ["meta", "WhatsApp Cloud API (Meta)"], ["infobip", "Infobip"], ["twilio", "Twilio"]]
                                 .filter(([v]) => v === "none" || v === msgs?.whatsapp_provider
                                   || (v === "meta" && msgs?.meta_ready) || (v === "infobip" && msgs?.infobip_whatsapp_ready)
                                   || (v === "twilio" && msgs?.twilio_ready))} />
              </div>

              <div className="space-y-2 border-t border-brass-light pt-4">
                <p className="smallcaps">Infobip {msgs?.infobip_api_key ? `· key saved (····${msgs.infobip_api_key_last4})` : "· no key yet"}</p>
                <input className="field" placeholder="API key" autoComplete="off" data-testid="infobip-api-key"
                       value={mform.infobip_api_key} onChange={(e) => setMform({ ...mform, infobip_api_key: e.target.value.trim() })} />
                <input className="field" placeholder="Base URL, e.g. https://xxxxx.api.infobip.com" data-testid="infobip-base-url"
                       value={mform.infobip_base_url} onChange={(e) => setMform({ ...mform, infobip_base_url: e.target.value.trim() })} />
                <div className="grid sm:grid-cols-2 gap-2">
                  <input className="field" placeholder="SMS sender, e.g. Fleurinelle" data-testid="infobip-sms-sender"
                         value={mform.infobip_sms_sender} onChange={(e) => setMform({ ...mform, infobip_sms_sender: e.target.value.trim() })} />
                  <input className="field" placeholder="WhatsApp sender number" data-testid="infobip-whatsapp-sender"
                         value={mform.infobip_whatsapp_sender} onChange={(e) => setMform({ ...mform, infobip_whatsapp_sender: e.target.value.trim() })} />
                </div>
                <p className="text-xs text-muted">Infobip gives you a personal base URL on your account home page — paste it exactly.</p>
              </div>

              <div className="space-y-2 border-t border-brass-light pt-4">
                <p className="smallcaps">Twilio {msgs?.twilio_auth_token ? `· token saved (····${msgs.twilio_auth_token_last4})` : "· no token yet"}</p>
                <input className="field" placeholder="Account SID (AC…)" autoComplete="off" data-testid="twilio-sid"
                       value={mform.twilio_account_sid} onChange={(e) => setMform({ ...mform, twilio_account_sid: e.target.value.trim() })} />
                <input className="field" placeholder="Auth token" autoComplete="off" data-testid="twilio-token"
                       value={mform.twilio_auth_token} onChange={(e) => setMform({ ...mform, twilio_auth_token: e.target.value.trim() })} />
                <div className="grid sm:grid-cols-2 gap-2">
                  <input className="field" placeholder="Sender number, e.g. +1…" data-testid="twilio-from"
                         value={mform.twilio_from_number} onChange={(e) => setMform({ ...mform, twilio_from_number: e.target.value.trim() })} />
                  <input className="field" placeholder="Messaging service SID (optional)" data-testid="twilio-service-sid"
                         value={mform.twilio_messaging_service_sid} onChange={(e) => setMform({ ...mform, twilio_messaging_service_sid: e.target.value.trim() })} />
                </div>
              </div>

              <div className="space-y-2 border-t border-brass-light pt-4">
                <p className="smallcaps">WhatsApp Cloud API (Meta) {msgs?.meta_access_token ? `· token saved (····${msgs.meta_access_token_last4})` : "· no token yet"}</p>
                <input className="field" placeholder="Access token" autoComplete="off" data-testid="meta-token"
                       value={mform.meta_access_token} onChange={(e) => setMform({ ...mform, meta_access_token: e.target.value.trim() })} />
                <div className="grid sm:grid-cols-2 gap-2">
                  <input className="field" placeholder="Phone number ID" data-testid="meta-phone-id"
                         value={mform.meta_phone_number_id} onChange={(e) => setMform({ ...mform, meta_phone_number_id: e.target.value.trim() })} />
                  <input className="field" placeholder="Template name (order_ready)" data-testid="meta-template"
                         value={mform.meta_template_name} onChange={(e) => setMform({ ...mform, meta_template_name: e.target.value.trim() })} />
                </div>
              </div>

              <div className="flex flex-wrap gap-2 pt-1">
                <button className="btn-primary text-xs px-4" data-testid="messaging-save" onClick={() => saveMessaging()}>Save provider keys</button>
                {msgs?.infobip_api_key ? <button className="btn-ghost text-xs px-3" data-testid="messaging-remove-infobip" onClick={() => removeMessaging("infobip")}>Remove Infobip</button> : null}
                {msgs?.twilio_auth_token ? <button className="btn-ghost text-xs px-3" data-testid="messaging-remove-twilio" onClick={() => removeMessaging("twilio")}>Remove Twilio</button> : null}
                {msgs?.meta_access_token ? <button className="btn-ghost text-xs px-3" data-testid="messaging-remove-meta" onClick={() => removeMessaging("meta")}>Remove Meta</button> : null}
              </div>

              <div className="grid sm:grid-cols-2 gap-2 border-t border-brass-light pt-4">
                <div className="flex gap-2">
                  <input className="field flex-1" placeholder="Test SMS to +212…" data-testid="settings-test-phone"
                         value={testPhone} onChange={(e) => setTestPhone(e.target.value)} />
                  <button className="btn-ghost text-xs px-3" data-testid="settings-test-sms" onClick={sendTestSms} disabled={!testPhone}>Test SMS</button>
                </div>
                <div className="flex gap-2">
                  <input className="field flex-1" placeholder="Test WhatsApp to +212…" data-testid="settings-test-whatsapp-to"
                         value={testWa} onChange={(e) => setTestWa(e.target.value)} />
                  <button className="btn-ghost text-xs px-3" data-testid="settings-test-whatsapp" onClick={sendTestWhatsapp} disabled={!testWa}>Test WhatsApp</button>
                </div>
              </div>
              {msg ? <p className="text-xs text-ink" data-testid="settings-message">{msg}</p> : null}
            </div>
          </div>
        ) : null}

        {group === "alerts" ? (
          <div className="card p-5 space-y-5" data-testid="settings-alerts">
            <div>
              <h3 className="smallcaps">What florists read</h3>
              <p className="text-xs text-muted mt-1">
                These are the WhatsApp and SMS messages sent to shops. Placeholders available: {alertsCfg?.placeholders?.join(" ")}
              </p>
            </div>
            {alertsCfg ? Object.keys(alertsCfg.templates).map((name) => (
              <div key={name} className="space-y-2 border-t border-brass-light pt-4" data-testid={`alert-block-${name}`}>
                <p className="smallcaps">{alertsCfg.labels[name]}</p>
                <textarea className="field min-h-[72px]" data-testid={`alert-input-${name}`}
                          value={aform[name] ?? alertsCfg.templates[name]}
                          onChange={(e) => setAform({ ...aform, [name]: e.target.value })} />
                <p className="text-xs text-muted" data-testid={`alert-preview-${name}`}>
                  Preview: {previewAlert(aform[name] ?? alertsCfg.templates[name])}
                </p>
                <button className="btn-ghost text-[11px] py-1 px-2" data-testid={`alert-reset-${name}`}
                        onClick={() => resetAlert(name)}>Reset to the standard wording</button>
              </div>
            )) : <p className="text-sm text-muted">Loading…</p>}
            <div className="flex gap-2 items-center pt-1">
              <button className="btn-primary text-xs px-4" data-testid="alerts-save" onClick={saveAlerts}>Save wording</button>
              {msg ? <span className="text-xs text-ink" data-testid="settings-message">{msg}</span> : null}
            </div>
          </div>
        ) : null}

        {group === "seo" ? (
          <div className="space-y-6" data-testid="settings-seo">
            <SearchVisibilityPanel />
            <div className="card p-5 space-y-3">
              <h3 className="smallcaps">SEO writer (your own Claude key)</h3>
              <p className="text-sm" data-testid="ai-status">
                {seo?.ai?.configured ? `Claude connected · key ····${seo.ai.key_last4} · ${seo.ai.model}` : "No Anthropic key saved yet — paste one to write metadata with one click."}
              </p>
              <input className="field" type="password" placeholder="sk-ant-…" autoComplete="off" data-testid="ai-key-input"
                     value={aiKey} onChange={(e) => setAiKey(e.target.value.trim())} />
              <div className="flex flex-wrap gap-2">
                <button className="btn-primary text-xs px-4" data-testid="ai-key-save" onClick={saveAiKey} disabled={!aiKey}>Save key</button>
                {seo?.ai?.configured ? <button className="btn-ghost text-xs px-3" data-testid="ai-key-remove" onClick={removeAiKey}>Remove key</button> : null}
              </div>
              <p className="text-xs text-muted">Get a key from console.anthropic.com → API keys. Usage is billed to your own Anthropic account; the key is stored server-side and never shown again.</p>
              <ClaudeModelSelect value={claudeModel} onChange={setClaudeModel} testid="settings-claude-model" saveDefault refreshKey={seo?.ai?.updated_at || ""} onSaved={() => loadSeo()} disabled={!!writing} />
            </div>

            <div className="card p-5 space-y-3">
              <h3 className="smallcaps">Feeds and crawling</h3>
              {seo?.feeds ? Object.entries(seo.feeds).map(([name, path]) => (
                <p key={name} className="text-sm">
                  <a className="text-brass hover:underline" href={path} target="_blank" rel="noreferrer" data-testid={`feed-${name}`}>{name.replace(/_/g, " ")}</a>
                  <span className="text-xs text-muted"> — {path}</span>
                </p>
              )) : null}
              <p className="text-xs text-muted">Paste the Google Merchant URL into Merchant Center → Products → Feeds, and the sitemap into Search Console.</p>
            </div>

            <div className="card p-5 space-y-3">
              <div className="flex flex-wrap gap-2 items-center">
                <h3 className="smallcaps flex-1">Product metadata</h3>
                <label className="text-xs text-muted flex items-center gap-1">
                  <input type="checkbox" data-testid="seo-missing-only" checked={missingOnly}
                         onChange={(e) => { setMissingOnly(e.target.checked); loadSeo(e.target.checked); }} />
                  only bouquets missing metadata
                </label>
              </div>
              {seo?.products?.slice(0, 40).map((p) => (
                <div key={p.id} className="border-t border-brass-light pt-2 text-sm flex flex-wrap gap-2 items-start" data-testid={`seo-row-${p.id}`}>
                  <div className="flex-1 min-w-[220px]">
                    <p className="text-ink">{p.title} <span className="text-xs text-muted">· {p.status}</span></p>
                    <p className="text-xs text-muted">{p.meta_title || "no title"} — {p.meta_description || "no description"}</p>
                  </div>
                  <button className="btn-ghost text-[11px] py-1 px-2" data-testid={`seo-generate-${p.id}`}
                          disabled={!seo?.ai?.configured || writing === p.id} onClick={() => generateSeo(p.id)}>
                    {writing === p.id ? "Writing…" : "Write with Claude"}
                  </button>
                </div>
              ))}
              {seo && !seo.products?.length ? <p className="text-xs text-muted">Every bouquet has metadata.</p> : null}
              {msg ? <p className="text-xs text-ink" data-testid="seo-message">{msg}</p> : null}
            </div>
          </div>
        ) : null}

        {group === "operations" ? (
          <div className="card p-5 space-y-4" data-testid="settings-operations">
            <h3 className="smallcaps">Order routing</h3>
            <Field label="Minutes a florist has to accept" testid="settings-offer-minutes" value={form.offer_expiry_minutes} onChange={set("offer_expiry_minutes")} />
            <Field label="Offers before it lands on the ops board" testid="settings-max-offers" value={form.max_offers_before_escalation} onChange={set("max_offers_before_escalation")} />
            <Field label="Weekly driver auto-payout day" testid="settings-payout-day" value={form.driver_payout_day} onChange={set("driver_payout_day")}
                   options={[["", "Off"], ["0", "Monday"], ["1", "Tuesday"], ["2", "Wednesday"], ["3", "Thursday"], ["4", "Friday"], ["5", "Saturday"], ["6", "Sunday"]]}
                   hint="On this day, drivers' accrued wages are auto-bundled into a payout request." />
            <Field label="Network assign lead time (minutes)" testid="settings-network-lead" value={form.network_assign_lead_minutes} onChange={set("network_assign_lead_minutes")}
                   hint="Network drivers can only claim/receive an order this many minutes before its delivery window starts." />

            <h3 className="smallcaps pt-2">Back-office security</h3>
            <Field label="Hours a back-office session lasts" testid="settings-session-hours" value={form.admin_session_hours} onChange={set("admin_session_hours")}
                   hint="After this many hours the team has to sign in again. Applies to every staff role." />
            <Field label="Hours a verified browser is trusted for 2FA" testid="settings-mfa-trust-hours" value={form.mfa_trust_hours} onChange={set("mfa_trust_hours")}
                   hint="Inside this window a fresh sign-in skips the 2FA code on the same browser. Set 0 to ask every time." />
            <Field label="Ops alert phone (SLA WhatsApp)" testid="settings-ops-phone" value={form.ops_alert_phone} onChange={set("ops_alert_phone")}
                   hint="Receives an alert when a delivery goes at-risk or breaches its window (needs Twilio/Infobip)." />
            <Field label="Ops WhatsApp number (optional)" testid="settings-ops-whatsapp" value={form.ops_alert_whatsapp} onChange={set("ops_alert_whatsapp")}
                   hint="If left empty, WhatsApp alerts go to the ops alert phone above." />
            <Field label="Alert ops when a card is still unpaid after (minutes)" testid="settings-stuck-hold-minutes"
                   value={form.stuck_hold_alert_minutes} onChange={set("stuck_hold_alert_minutes")}
                   hint="A florist has accepted but the card hold was never charged: we retry the capture, then e-mail, SMS and WhatsApp ops." />
            <SaveRow testid="settings-save-operations" />
          </div>
        ) : null}

        {group === "cutoffs" ? (
          <div className="card p-5 space-y-5" data-testid="settings-cutoffs">
            <div className="space-y-4">
              <h3 className="smallcaps">Global same-day cut-offs</h3>
              <p className="text-xs text-muted">Each same-day window stays orderable until its cut-off passes (Morocco time). Later windows can stay open longer. Applies to every city unless overridden below.</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Field label="Morning · 09:00–13:00" testid="settings-cutoff-morning" value={form.cutoff_morning} onChange={set("cutoff_morning")} hint="e.g. 08:00" />
                <Field label="Afternoon · 13:00–18:00" testid="settings-cutoff-afternoon" value={form.cutoff_afternoon} onChange={set("cutoff_afternoon")} hint="e.g. 13:00" />
                <Field label="Evening · 18:00–21:00" testid="settings-cutoff-evening" value={form.cutoff_evening} onChange={set("cutoff_evening")} hint="e.g. 17:00" />
              </div>
              <SaveRow testid="settings-save-cutoffs" />
            </div>
            <div className="space-y-3 pt-4 border-t border-brass-light">
              <h3 className="smallcaps">Per-city overrides</h3>
              <CityCutoffEditor defaults={{ morning: form.cutoff_morning, afternoon: form.cutoff_afternoon, evening: form.cutoff_evening }} />
            </div>
          </div>
        ) : null}

        {group === "maps" ? (
          <div className="card p-5 space-y-4" data-testid="settings-maps">
            <h3 className="smallcaps">Maps & location</h3>
            <p className="text-xs text-muted">
              The Google Maps key is loaded in the browser to show the delivery pin map and Morocco
              address autocomplete. Restrict it in Google Cloud Console to your domain
              (HTTP referrers) and to the Maps JavaScript, Places and Geocoding APIs.
            </p>
            <Field label="Google Maps API key" testid="settings-maps-key" value={form.google_maps_api_key}
                   onChange={set("google_maps_api_key")} hint="Leave empty to hide the map. In Google Cloud enable BOTH 'Maps JavaScript API' and 'Places API (New)' on this key — address suggestions now use the new Places API, as the old one is being retired. Without it the map and pin still work and buyers type the address by hand." />
            <Field label="URL signing secret" testid="settings-maps-signing-secret" value={form.google_maps_signing_secret}
                   onChange={set("google_maps_signing_secret")}
                   hint="Google Cloud → Credentials → your Maps key → URL signing secret. Kept server-side only to sign Maps web-service requests (Static Maps / Geocoding). Optional — leave empty for unsigned usage." />
            <Field label="Detect visitor country (phone dial-code default)" testid="settings-geolocation"
                   value={form.geolocation_enabled} onChange={set("geolocation_enabled")}
                   options={[["on", "On"], ["off", "Off"]]} hint="Search engines and AI crawlers are never geolocated." />
            <Field label="Morocco address autocomplete" testid="settings-autocomplete"
                   value={form.address_autocomplete_enabled} onChange={set("address_autocomplete_enabled")}
                   options={[["on", "On"], ["off", "Off"]]} />
            <Field label="Delivery pin & recipient location request" testid="settings-delivery-pin"
                   value={form.delivery_pin_enabled} onChange={set("delivery_pin_enabled")}
                   options={[["on", "On"], ["off", "Off"]]} hint="Lets buyers drop a pin or ask the recipient to share their exact location." />
            <SaveRow testid="settings-save-maps" />
          </div>
        ) : null}


        {group === "company" ? (
          <div className="card p-5 space-y-4" data-testid="settings-company">
            <h3 className="smallcaps">Company & legal</h3>
            <Field label="Legal entity" testid="settings-legal-name" value={form.legal_name} onChange={set("legal_name")} />
            <Field label="Registered address" testid="settings-legal-address" value={form.legal_address} onChange={set("legal_address")}
                   hint="Shown in the storefront footer under the Fleurinelle name." />
            <Field label="VAT / tax number" testid="settings-vat-id" value={form.vat_id} onChange={set("vat_id")} hint="Optional. Added to the footer when set." />
            <SaveRow testid="settings-save-company" />
          </div>
        ) : null}
        {group === "security" ? (
          <div className="card p-5 space-y-4" data-testid="settings-security">
            <h3 className="smallcaps">Security & domains</h3>
            <p className="text-xs text-muted">
              Only these front-end origins may call the API from a browser (CORS) or receive
              password-reset / checkout links. The production domains and this preview are always
              allowed. Add any future domain here — one per line or comma-separated, with
              <code> https://</code>.
            </p>
            <label className="block space-y-1">
              <span className="smallcaps">Additional allowed origins</span>
              <textarea className="field min-h-[96px]" data-testid="settings-extra-origins" value={form.extra_allowed_origins}
                        onChange={(e) => set("extra_allowed_origins")(e.target.value)}
                        placeholder={"https://shop.fleurinelle.com\nhttps://app.fleurinelle.ma"} />
            </label>
            <div className="text-xs text-muted" data-testid="settings-effective-origins">
              <span className="smallcaps">Currently allowed</span>
              <ul className="mt-1 space-y-0.5">
                {(settings.allowed_origins_effective || []).map((o) => <li key={o} className="font-mono">{o}</li>)}
              </ul>
            </div>
            <div className="text-xs text-muted space-y-1">
              <p>Also enforced automatically: admin sessions expire after 4 hours (12 h for shops, drivers and buyers);
              every login, 2FA, password-reset and signup endpoint is rate-limited; uploads are scanned by content;
              HSTS, nosniff, frame-denial and referrer headers are sent on every API response.</p>
            </div>
            <SaveRow testid="settings-save-security" />
          </div>
        ) : null}
        {group === "danger" ? <DangerZone /> : null}
      </div>
    </div>
  );
};

// -------- Danger zone — pre-launch data purge + debug capture --------

const DangerZone = () => {
  const [confirmation, setConfirmation] = useState("");
  const [alsoProducts, setAlsoProducts] = useState(true);
  const [purgeBusy, setPurgeBusy] = useState(false);
  const [purgeMsg, setPurgeMsg] = useState("");
  const [debug, setDebug] = useState(null);
  const [debugHours, setDebugHours] = useState(24);
  const [debugBusy, setDebugBusy] = useState(false);
  const [syncBusy, setSyncBusy] = useState(false);
  const [syncMsg, setSyncMsg] = useState("");
  const REQUIRED = "PURGE ALL LIVE DATA";

  const loadDebug = () => api.get("/admin/dev/debug-mode").then((r) => setDebug(r.data)).catch(() => {});
  useEffect(() => { loadDebug(); }, []);

  // Poll a background partner job until it finishes. The POST returns instantly
  // with a job_id so we never hit the 120 s ingress read-timeout on long scrapes.
  const pollPartnerJob = async (jobId, setMsg, maxMs = 8 * 60 * 1000) => {
    const started = Date.now();
    let ticks = 0;
    while (Date.now() - started < maxMs) {
      await new Promise((r) => setTimeout(r, 3000));
      ticks += 1;
      let job;
      try {
        job = (await api.get(`/admin/partners/jobs/${jobId}`)).data;
      } catch (e) { continue; }  // transient network blip — keep polling
      if (job.status === "done") return job;
      if (job.status === "error") throw new Error(job.error || "Job failed");
      setMsg(`Working… (${ticks * 3}s elapsed)`);
    }
    throw new Error("Job is taking longer than expected — check back shortly.");
  };

  const runPartnerSync = async () => {
    setSyncBusy(true); setSyncMsg("Starting sync… pulling live catalogues from Sophinia + Golden Flower.");
    try {
      const { data } = await api.post("/admin/partners/sync-real-florists");
      const data2 = await pollPartnerJob(data.job_id, setSyncMsg);
      const res = data2.result || {};
      const bits = [];
      for (const key of ["sophinia", "golden_flower"]) {
        const r = res[key] || {};
        if (r.error) bits.push(`${key}: ${r.error}`);
        else bits.push(`${key}: ${r.action}, ${r.inserted} products across ${(r.cities || []).length} cities${r.login_email ? ` · login ${r.login_email}` : ""}`);
      }
      setSyncMsg("Sync OK · " + bits.join("  ·  "));
    } catch (e) { setSyncMsg(errText(e)); }
    finally { setSyncBusy(false); }
  };

  const [enrichBusy, setEnrichBusy] = useState(false);
  const [enrichModel, setEnrichModel] = useState("");
  const [enrichMsg, setEnrichMsg] = useState("");
  const runEnrichPartners = async () => {
    // AI rewrite is heavy (~25 min for 60 bouquets). Warn the operator before starting.
    if (!window.confirm(
      "Rewrite every Sophinia + Golden Flower bouquet's occasions, description, meta title, "
      + "meta description using the selected Claude model. Existing URLs will stay unchanged.\n\n"
      + "This is billed to your saved Anthropic account. "
      + "It runs in the background — you can leave this tab, but keep it open to see the result. Continue?"
    )) return;
    setEnrichBusy(true); setEnrichMsg("Rewriting… this runs in the background and can take up to 30 minutes.");
    try {
      const { data } = await api.post("/admin/partners/enrich-descriptions", { model: enrichModel || null });
      const data2 = await pollPartnerJob(data.job_id, setEnrichMsg, 30 * 60 * 1000);
      const res = data2.result || {};
      const bits = Object.entries(res.per_shop || {})
        .map(([slug, r]) => `${slug}: ${r.ok}/${r.total} rewritten (${r.skipped} skipped, ${r.failed} failed)`);
      setEnrichMsg(`Done in ${res.duration_s || "?"}s · ` + bits.join("  ·  "));
    } catch (e) { setEnrichMsg(errText(e)); }
    finally { setEnrichBusy(false); }
  };

  const runPurge = async () => {
    if (confirmation.trim() !== REQUIRED) return;
    if (!window.confirm(`Last chance. This deletes every order, customer, florist, driver, application, payment and log on ${window.location.host}. Continue?`)) return;
    setPurgeBusy(true); setPurgeMsg("");
    try {
      const { data } = await api.post("/admin/dev/purge-live-data", { confirmation, also_products: alsoProducts });
      const parts = Object.entries(data.deleted).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]).slice(0, 10);
      setPurgeMsg("Purged · " + parts.map(([k, v]) => `${k}:${v}`).join("  ·  ") + (parts.length === 10 ? " …" : ""));
      setConfirmation("");
    } catch (e) { setPurgeMsg(errText(e)); }
    finally { setPurgeBusy(false); }
  };

  const toggleDebug = async (enabled) => {
    setDebugBusy(true);
    try {
      await api.post("/admin/dev/debug-mode", { enabled, hours: debugHours });
      await loadDebug();
    } catch (e) { setPurgeMsg(errText(e)); }
    finally { setDebugBusy(false); }
  };

  const downloadLog = async () => {
    try {
      const res = await api.get("/admin/dev/debug-log", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "fleurinelle-debug.md";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { setPurgeMsg(errText(e)); }
  };

  return (
    <div className="space-y-6" data-testid="settings-danger-zone">
      <div className="card p-5 border border-brass-light bg-plaster-deep/40 space-y-4" data-testid="partner-sync">
        <div>
          <h3 className="smallcaps text-brass">Real partner florists · Sophinia + Golden Flower</h3>
          <p className="text-sm text-charcoal mt-2">
            Pulls each partner's live catalogue from their public product API
            (<code className="bg-plaster px-1">sophinia.com</code> WooCommerce ·
            <code className="bg-plaster px-1 ms-1">goldenflower.ma</code> Shopify),
            filters out vases / chocolates / plush / subscriptions, and inserts every
            bouquet — Sophinia in Tangier only, Golden Flower in the platform cities
            they actually deliver to. Idempotent — run this after every deploy to bring
            production in sync with what buyers see on the partners' own sites. Their
            <b> orders, customers and payments are never touched.</b>
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button className="btn-primary text-xs disabled:opacity-50" data-testid="partner-sync-run"
                  disabled={syncBusy} onClick={runPartnerSync}>
            {syncBusy ? "Syncing…" : "Sync partner florists now"}
          </button>
          <button className="btn-ghost text-xs disabled:opacity-50" data-testid="partner-enrich-run"
                  disabled={enrichBusy} onClick={runEnrichPartners}
                  title="Uses Claude vision to rewrite occasions, descriptions and SEO for every imported bouquet — ~25 min.">
            {enrichBusy ? "Rewriting descriptions…" : "Rewrite descriptions & SEO"}
          </button>
        </div>
        {syncMsg ? <p className="text-xs text-charcoal break-words" data-testid="partner-sync-msg">{syncMsg}</p> : null}
        <ClaudeModelSelect value={enrichModel} onChange={setEnrichModel} testid="enrich-claude-model" disabled={enrichBusy} />
        {enrichMsg ? <p className="text-xs text-charcoal break-words" data-testid="partner-enrich-msg">{enrichMsg}</p> : null}
      </div>

      <div className="card p-5 border border-danger/40 bg-danger/5 space-y-4" data-testid="danger-purge">
        <div>
          <h3 className="smallcaps text-danger">Purge live data (pre-launch reset)</h3>
          <p className="text-sm text-charcoal mt-2">
            Wipes every <b>order, customer, payment, log, florist, driver, application, review, cart and notification</b>.
            Preserves settings, roles, API keys, cities/zones/occasions, addons, articles, categories, admin logins and legal
            documents so the platform stays ready for real signups the moment you launch. Indexes are kept intact.
          </p>
          <p className="text-xs text-muted mt-2">
            Type the exact phrase <code className="bg-plaster-deep px-1">{REQUIRED}</code> below to unlock the button.
          </p>
        </div>
        <label className="inline-flex items-center gap-2 text-xs text-ink">
          <input type="checkbox" data-testid="danger-also-products" checked={alsoProducts}
                 onChange={(e) => setAlsoProducts(e.target.checked)} />
          Also purge seeded products (recommended — they belong to demo florists)
        </label>
        <input data-testid="danger-confirmation" className="field w-full" placeholder={REQUIRED}
               value={confirmation} onChange={(e) => setConfirmation(e.target.value)} />
        <button className="btn-primary bg-danger hover:bg-danger disabled:opacity-50"
                data-testid="danger-purge-button"
                disabled={confirmation.trim() !== REQUIRED || purgeBusy} onClick={runPurge}>
          {purgeBusy ? "Purging…" : "Purge live data now"}
        </button>
        {purgeMsg ? <p className="text-xs text-charcoal break-words" data-testid="danger-purge-message">{purgeMsg}</p> : null}
      </div>

      <div className="card p-5 border border-warning/40 bg-warning/5 space-y-4" data-testid="danger-debug">
        <div>
          <h3 className="smallcaps text-warning">Debug capture mode</h3>
          <p className="text-sm text-charcoal mt-2">
            When enabled, every 4xx and 5xx response — plus any uncaught exception — is appended to a
            private Markdown log with endpoint, method, status, query, client IP and full traceback.
            Send the file to Fleurinelle support and they'll know exactly what broke.
          </p>
        </div>
        <div className="grid sm:grid-cols-3 gap-3 items-end">
          <label className="block">
            <span className="text-xs text-muted uppercase tracking-wider">Duration (hours)</span>
            <input type="number" min="1" max="168" data-testid="debug-hours" className="field w-full mt-1"
                   value={debugHours} onChange={(e) => setDebugHours(Number(e.target.value) || 24)} />
          </label>
          <div className="text-xs">
            {debug?.enabled ? (
              <div className="text-warning" data-testid="debug-status-on">
                <div><b>Active</b> · expires {debug.expires_at?.slice(0, 16).replace("T", " ")}</div>
                <div>{debug.log_lines} lines captured</div>
              </div>
            ) : (
              <span className="text-muted" data-testid="debug-status-off">Off. Nothing is being captured.</span>
            )}
          </div>
          <div className="flex gap-2 justify-end">
            {debug?.enabled ? (
              <button className="btn-ghost text-xs" data-testid="debug-stop" disabled={debugBusy}
                      onClick={() => toggleDebug(false)}>Stop</button>
            ) : (
              <button className="btn-primary text-xs" data-testid="debug-start" disabled={debugBusy}
                      onClick={() => toggleDebug(true)}>Start capture</button>
            )}
            <a href="/api/admin/dev/debug-log" data-testid="debug-download"
               className="btn-ghost text-xs cursor-pointer" onClick={(e) => { e.preventDefault(); downloadLog(); }}>Download log</a>
          </div>
        </div>
      </div>
    </div>
  );
};
